/* -----------------------------------------------------------------------------------------------------
  - Created: [31-01-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains Category Level list and modals
  -------------------------------------------------------------------------------------------------------*/
import React, { useEffect, useState } from 'react'
import './CertificationTableStyle.css'
import CustomBottomPagination from '../../../../Components/custom_components/CustomBottomPagination/CustomBottomPagination'
import CustomTable from '../../../../Components/custom_components/CustomTable/CustomTable'
import CustomModal from '../../../../Components/custom_components/CustomModal/CustomModal'
import { ErrorToast, InfoToast, SuccessToast } from '../../../../utils/toast'
import CustomDeleteConfirmModal from '../../../../Components/custom_components/CustomDeleteConfirmModal/CustomDeleteConfirmModal'
import { handlePaginationEntries } from '../../../../utils/pagination'
import CertificationSearchFilter from '../CertificationSearchFilter/CertificationSearchFilter'
import CertificationForm from '../CertificationForm/CertificationForm'
import { categories_creation, categories_deletion, categories_update, get_categories_by_id, get_categories_with_pagination } from '../../../../config/SettingsApis/categorieApis'
import { useSelector } from 'react-redux'
import { hasPrivilege } from '../../../../utils/privileges'
import Loader from '../../../../Components/Loader/Loader'
import { Icons } from '../../../../utils/GlobalIcons'
import CustomTooltipWithoutRoundBorder from '../../../../Components/CustomActionButtonWithTooltip/CustomTooltipWithoutRoundBorder'
import { getStatusColors } from '../../../../utils/constants'

function CertificationTable() {
    const privileges = useSelector((state) => state.privileges.privileges);


    const [listing, setListing] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalItemPerPage, setTotalItemPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [refresh, setRefresh] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false); // Loader state

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState({
        isOpen: false,
        id: '',
    });
    const [modalProps, setModalProps] = useState({
        isOpen: false,
        id: '',
        formValues: {}
    });

    const [pagination, setPagination] = useState({
        start: 0,
        end: 0,
        totalEntries: 0,
    });

    useEffect(() => {
        get_categories()
    }, [pageNumber, totalItemPerPage, refresh])

    // GET CATEGORIES LIST 
    const get_categories = async () => {
        const formData = new FormData();
        formData.append('page', pageNumber);
        formData.append('pageItemcount', totalItemPerPage);

        try {
            setIsLoading(true)
            const response = await get_categories_with_pagination(formData)
            if (response?.status === "success") {
                setListing(response.data.data)
                setTotalPages(response.data.last_page)
                const updatedPagination = handlePaginationEntries(response.data.from, response.data.to, response.data.total)
                setPagination(updatedPagination);
            } else {
                setListing([])
            }
        } catch (error) {
            setListing([])
        } finally {
            setIsLoading(false)
        }
    }


    // Define the required permission actions
    const PERMISSION_ACTIONS = {
        view: 4,
        edit: 5,
        history: 6,
        add: 3,
    };

    // Map permissions to boolean values based on privileges
    const userPermissions = {
        view: hasPrivilege(privileges, "Settings", 13, PERMISSION_ACTIONS.view),
        edit: hasPrivilege(privileges, "Settings", 13, PERMISSION_ACTIONS.edit),
        history: hasPrivilege(privileges, "Settings", 13, PERMISSION_ACTIONS.history),
        add: hasPrivilege(privileges, "Settings", 13, PERMISSION_ACTIONS.add),
    };
    // / Function to check if any action-related permissions are granted
    const hasActionPermissions = userPermissions.edit


    const columns = [
        {
            title: "#",
            key: "key",
        },
        {
            title: "Category",
            key: "category",
        }, {
            title: "Description",
            key: "descrption",
        },
        {
            title: "Status",
            key: "status",
            render: (_, record) => {
                //  actual status text
                const statusText = record?.status || "Unknown";

                const statusCode = statusText.toLowerCase() === "active" ? 1 : statusText.toLowerCase() === "inactive" ? 2 : '';
                const style = getStatusColors(statusCode);

                return (
                    <span className="status-badge" style={style}>
                        {statusText}
                    </span>
                );
            },
        },
        { title: "Actions", key: "actions", render: (text, row) => renderActions(row) },
    ].filter(column => column.key !== "actions" || hasActionPermissions); //removes the "Actions" column when hasActionPermissions is false

    // Render custom actions based on user role
    const renderActions = (row) => {
        return (
            <div className='action-container'>
                {userPermissions.edit &&
                    <CustomTooltipWithoutRoundBorder title="Edit">
                        <Icons.Edit className="edit-action-icon" onClick={() => handleModal(true, row?.id)} />
                    </CustomTooltipWithoutRoundBorder>
                }
                {/* <FaTrashAlt className="delete-action-icon" onClick={() => handlDeleteModal(true, row?.id)} /> */}
            </div>

        );
    };

    const datas = () => listing?.map((item, index) => ({
        key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
        id: item.CLL_Id,
        category: item.Name,
        status: item?.statusName,
        descrption: item?.Description,
    }));

    // for open and close add or update modal
    const handleModal = async (isOpen, CategoryLevelID) => {
        if (CategoryLevelID) {
            const params = { CategoryLevelID }
            getCategoryLevelById(params)
        } else {
            setModalProps({
                isOpen,
                id: '',
                formValues: { Name: '', status: 1, Description: '' }
            })
        }
    };

    // GET category levels by id
    const getCategoryLevelById = async (params) => {
        try {
            const response = await get_categories_by_id(params)
            if (response?.status === "success") {

                setModalProps({
                    isOpen: true,
                    id: response?.data?.CLL_Id,
                    formValues: {
                        Name: response?.data?.CLL_Name,
                        status: response?.data?.CTL_status,
                        Description: response?.data?.CTL_Description || ""
                    }
                });
            } else {
                ErrorToast(response?.message || `Something Went Wrong!`)
            }
        } catch (error) {
            ErrorToast(`Something Went Wrong!`)
        }
    }

    const handlDeleteModal = (isOpen, id = '') => {
        setIsDeleteModalOpen({ isOpen, id })
    }

    // FOR SAVE AND UPDATE CATEGORY LEVEL
    const handleCategoryLevelSave = async (values, CategoryLevelID) => {
        const formData = new FormData();
        for (const key in values) {
            formData.append(key, values[key]);
        }

        try {
            setIsButtonDisabled(true);

            if (CategoryLevelID) {
                formData.append('CategoryLevelID', CategoryLevelID);
            }

            const apiFunction = CategoryLevelID ? categories_update : categories_creation;
            const response = await apiFunction(formData);

            const message = response?.data?.message || 'Something went wrong!';
            const status = response?.data?.status;
            if (status === "success") {
                SuccessToast(message);
                handleModal(false);
                setRefresh(!refresh);
            } else {
                ErrorToast(message);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsButtonDisabled(false);
        }
    };
    // FOR DELETE CATEGORY LEVEL BY ID
    const handleDeleteCategory = async () => {
        const params = { CategoryLevelID: isDeleteModalOpen?.id }
        try {
            const response = await categories_deletion(params)

            if (response?.data?.status === "success") {
                SuccessToast(response?.data?.message)
                handlDeleteModal(false, '')
                setRefresh(!refresh)
            } else {
                ErrorToast(response?.data?.message || `Something Went Wrong!`)
            }
        } catch (error) {
            ErrorToast(`Something Went Wrong!`)
        }
    }

    const updatePagination = (pageNumber) => setPageNumber(pageNumber);
    const updateItemPageCount = (pageItemCount) => setTotalItemPerPage(pageItemCount);

    return (
        <div className="Certification-table-container">
            <CertificationSearchFilter tableTitle="Category Level"
                isAddEditModalOpen={modalProps.isOpen}
                handleAddEditModal={handleModal}
                isAddPermission={userPermissions?.add}
            />
            <CustomTable columns={columns} rows={isLoading ? [] : datas()} isLoading={isLoading} />

            {isLoading ? <Loader /> : <CustomBottomPagination
                items_per_page={totalItemPerPage}
                current_page={pageNumber}
                total_page_count={totalPages}
                on_change_value={updatePagination}
                on_page_count_update={updateItemPageCount}
                pagination={pagination}
            />
            }
            {modalProps.isOpen && <CustomModal
                open={modalProps.isOpen}
                handleClose={() => handleModal(false)}
                title={modalProps.id !== '' ? "Edit Category Level" : "Create Category Level"}
                body={<CertificationForm
                    isButtonDisabled={isButtonDisabled}
                    handleSave={handleCategoryLevelSave}
                    id={modalProps.id}
                    formIntialValues={modalProps.formValues}
                />}
                maxWidth="1000px"
            />}
            {isDeleteModalOpen?.isOpen && <CustomDeleteConfirmModal
                module_name={'Category Level'}
                open_modal={isDeleteModalOpen?.isOpen}
                close_modal={handlDeleteModal}
                confirm_function={handleDeleteCategory}
            />}

        </div>
    );
}

export default CertificationTable;
